import { useEffect, useState } from 'react';
import { api, ApiError, getToken } from '../api/client';
import { urlImpression } from '../api/config';
import { useAuth } from '../state/auth';
import { Bandeau, Carte, Champ, Squelette, Vide } from '../components/ui';
import { centimes, dateFr, deviseReference, montant } from '../lib/format';
import { T } from '../i18n';
import type { Vehicule } from '../api/types';

interface Calcul { partLocataireMinor: number; regleAppliquee: string; detailCalcul: string; base: { reparation: number; mainOeuvre: number; immobilisation: number } }
interface Sinistre {
  _id: string; date: string; partLocataireMinor: number; detailCalcul: string;
  cautionDeduiteMinor: number; resteADevoirMinor: number; reglee: boolean;
  circonstances: { ivresse: boolean; faitVolontaire: boolean; negligence: boolean; locataireFautif: boolean };
  vehicle?: { marque: string; modele: string; immatriculation: string };
  reservation?: { numero: string };
}

/** L'article 06, rendu automatique — et sa regle ecrite en toutes lettres. */
export default function Sinistres() {
  const { peut } = useAuth();
  const [liste, setListe] = useState<Sinistre[] | null>(null);
  const [vehicules, setVehicules] = useState<Vehicule[]>([]);
  const [calcul, setCalcul] = useState<Calcul | null>(null);
  const [erreur, setErreur] = useState('');
  const [msg, setMsg] = useState('');

  const [f, setF] = useState({
    vehicle: '', ivresse: false, faitVolontaire: false, negligence: false, locataireFautif: false,
    carrosserie: '', mecanique: '', mainOeuvre: '', jours: '', tarifJour: '', assurance: '',
    description: '',
  });

  useEffect(() => {
    api.get<Sinistre[]>('/operations/sinistres').then(setListe);
    api.get<Vehicule[]>('/vehicules').then(setVehicules);
  }, []);

  const entree = () => ({
    circonstances: { ivresse: f.ivresse, faitVolontaire: f.faitVolontaire, negligence: f.negligence, locataireFautif: f.locataireFautif },
    devisCarrosserieMinor: centimes(Number(f.carrosserie || 0)),
    devisMecaniqueMinor: centimes(Number(f.mecanique || 0)),
    mainOeuvreMinor: centimes(Number(f.mainOeuvre || 0)),
    joursImmobilisation: Number(f.jours || 0),
    tarifJourMinor: centimes(Number(f.tarifJour || 0)),
    priseEnChargeAssuranceMinor: centimes(Number(f.assurance || 0)),
  });

  useEffect(() => {
    const t = setTimeout(() => {
      api.post<Calcul>('/operations/sinistres/simuler', entree()).then(setCalcul).catch(() => setCalcul(null));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f]);

  if (!liste) return <Squelette lignes={6} />;

  async function enregistrer() {
    setErreur('');
    try {
      await api.post('/operations/sinistres', {
        ...entree(), vehicle: f.vehicle,
        description: f.description.trim() || undefined,
      });
      setMsg(T.sinistres.enregistre);
      api.get<Sinistre[]>('/operations/sinistres').then(setListe);
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.sinistres.erreur); }
  }

  const bascule = (k: keyof typeof f) => setF({ ...f, [k]: !f[k] } as typeof f);

  const ts = T.sinistres;
  const d = deviseReference();
  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{ts.titre}</h1>
          <p>{ts.sousTitre}</p>
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}

      <div className="grille g2" style={{ alignItems: 'start' }}>
        <Carte titre={ts.circonstancesMontants}>
          <Champ label={ts.vehicule}>
            <select value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: e.target.value, tarifJour: String((vehicules.find((v) => v._id === e.target.value)?.tarifHauteSaisonMinor ?? 0) / 100) })}>
              <option value="">{ts.choisir}</option>
              {vehicules.map((v) => <option key={v._id} value={v._id}>{v.marque} {v.modele} — {v.immatriculation}</option>)}
            </select>
          </Champ>

          <div style={{ marginTop: 14 }}>
            <div className="lbl" style={{ marginBottom: 6 }}>{ts.circonstances}</div>
            <div style={{ display: 'grid', gap: 6 }}>
              {(['ivresse', 'faitVolontaire', 'negligence', 'locataireFautif'] as const).map((k) => [k, ts.cases[k]] as const).map(([k, l]) => (
                <label key={k} style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '8px 10px', border: '1px solid var(--bord)', borderRadius: 'var(--r-champ)', cursor: 'pointer', background: f[k] ? 'var(--danger-doux)' : 'var(--surface)' }}>
                  <input type="checkbox" checked={f[k]} onChange={() => bascule(k)} />{l}
                </label>
              ))}
            </div>
            <p className="mini sourdine" style={{ marginTop: 6 }}>
              {ts.aucuneCase}
            </p>
          </div>

          <div className="grille g2" style={{ marginTop: 14 }}>
            <Champ label={ts.carrosserie(d)}><input type="number" value={f.carrosserie} onChange={(e) => setF({ ...f, carrosserie: e.target.value })} /></Champ>
            <Champ label={ts.mecanique(d)}><input type="number" value={f.mecanique} onChange={(e) => setF({ ...f, mecanique: e.target.value })} /></Champ>
            <Champ label={ts.mainOeuvreDevise(d)}><input type="number" value={f.mainOeuvre} onChange={(e) => setF({ ...f, mainOeuvre: e.target.value })} /></Champ>
            <Champ label={ts.jours}><input type="number" value={f.jours} onChange={(e) => setF({ ...f, jours: e.target.value })} /></Champ>
            <Champ label={ts.tarifJour(d)}><input type="number" value={f.tarifJour} onChange={(e) => setF({ ...f, tarifJour: e.target.value })} /></Champ>
            <Champ label={ts.assurance(d)}><input type="number" value={f.assurance} onChange={(e) => setF({ ...f, assurance: e.target.value })} /></Champ>
          </div>

          {/* Des cases a cocher ne racontent pas un accident. C'est ce texte
            * qu'on relit six mois plus tard, quand le locataire conteste. */}
          <div style={{ marginTop: 14 }}>
            <Champ label={ts.recit}
                   aide={ts.recitAide}>
              <textarea rows={4} value={f.description} maxLength={2000}
                        onChange={(e) => setF({ ...f, description: e.target.value })}
                        placeholder={ts.recitExemple} />
            </Champ>
          </div>
        </Carte>

        <Carte titre={ts.resultat} className="signature-kpi">
          {!calcul ? <Squelette lignes={3} /> : (
            <>
              <div className="micro">{ts.chargeLocataire}</div>
              <div className="kpi"><div className="v">{montant(calcul.partLocataireMinor)}</div></div>
              <div style={{ marginTop: 12 }}>
                <Bandeau type="info" titre={calcul.regleAppliquee}>{calcul.detailCalcul}</Bandeau>
              </div>
              <table className="tbl compact" style={{ marginTop: 12 }}>
                <tbody>
                  <tr><td>{ts.reparation}</td><td className="droite montant">{montant(calcul.base.reparation)}</td></tr>
                  <tr><td>{ts.mainOeuvre}</td><td className="droite montant">{montant(calcul.base.mainOeuvre)}</td></tr>
                  <tr><td>{ts.immobilisation}</td><td className="droite montant">{montant(calcul.base.immobilisation)}</td></tr>
                </tbody>
              </table>
              {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
              {peut('modifier.finance') ? (
                <button className="btn btn-p btn-l btn-bloc" style={{ marginTop: 14 }} disabled={!f.vehicle} onClick={() => void enregistrer()}>
                  {ts.enregistrer}
                </button>
              ) : (
                <p className="mini sourdine" style={{ marginTop: 12 }}>
                  {ts.reserve}
                </p>
              )}
            </>
          )}
        </Carte>
      </div>

      <div style={{ marginTop: 16 }}>
        <Carte titre={ts.enregistres} pad={false}>
          {liste.length === 0 ? <div className="carte-p"><Vide titre={ts.aucun} /></div> : (
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <thead><tr><th>{ts.col.date}</th><th>{ts.col.vehicule}</th><th>{ts.col.regle}</th><th className="droite">{ts.col.part}</th><th className="droite">{ts.col.caution}</th><th className="droite">{ts.col.reste}</th><th></th></tr></thead>
                <tbody>
                  {liste.map((s) => (
                    <tr key={s._id}>
                      <td className="num">{dateFr(s.date)}</td>
                      <td>{s.vehicle?.marque} {s.vehicle?.modele}<div className="mini sourdine">{s.vehicle?.immatriculation}</div></td>
                      <td className="mini sourdine">{s.detailCalcul.split('.')[0]}</td>
                      <td className="droite montant">{montant(s.partLocataireMinor)}</td>
                      <td className="droite montant">{montant(s.cautionDeduiteMinor)}</td>
                      <td className="droite montant">{montant(s.resteADevoirMinor)}</td>
                      {/* Le constat part chez l'assureur et chez le locataire :
                          recopier les chiffres a la main, c'est perdre le
                          remboursement sur une erreur de saisie. */}
                      <td className="droite">
                        <a className="btn btn-s" target="_blank" rel="noreferrer"
                           href={urlImpression(`/sinistres/${s._id}/imprimer`, getToken())}>{ts.constat}</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
      </div>

      <p className="mini sourdine" style={{ marginTop: 12, maxWidth: 800 }}>
        {ts.note}
      </p>
    </>
  );
}
